import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import {
  ChevronDown,
  X,
  Check,
  Search,
  Users,
  AlertCircle,
  Calculator,
  Calendar,
  ChevronRight,
  Printer,
} from 'lucide-react';
import { fadeInUp, staggerContainer } from '../utils/animations';

interface DoctorRecommendation {
  doctorName: string;
  amount: number;
  type: 'doctor' | 'recommender' | 'navigator' | 'campaign' | 'admin';
}

interface PaymentStatus {
  date: string;
  totalAmount: number;
  isPaid: boolean;
  recommendations: DoctorRecommendation[];
}

interface PaymentSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  payments: PaymentStatus[];
  showPaid?: boolean;
}

function PaymentSummaryModal({
  isOpen,
  onClose,
  payments,
  showPaid = false,
}: PaymentSummaryModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  // Get recommendations based on payment status
  const filteredRecommendations = payments
    .filter((payment) => (showPaid ? payment.isPaid : !payment.isPaid))
    .reduce((acc, payment) => {
      payment.recommendations.forEach((rec) => {
        const existing = acc.find((r) => r.doctorName === rec.doctorName);
        if (existing) {
          existing.amount += rec.amount;
        } else {
          acc.push({ ...rec });
        }
      });
      return acc;
    }, [] as DoctorRecommendation[]);

  // Combine doctor and recommender amounts for each doctor
  const combinedDoctorAmounts = filteredRecommendations.reduce((acc, rec) => {
    if (rec.type === 'doctor' || rec.type === 'recommender') {
      const existing = acc.find((d) => d.doctorName === rec.doctorName);
      if (existing) {
        existing.amount += rec.amount;
      } else {
        acc.push({
          doctorName: rec.doctorName,
          amount: rec.amount,
          type: 'doctor',
        });
      }
    } else {
      acc.push(rec);
    }
    return acc;
  }, [] as DoctorRecommendation[]);

  // Group recommendations by type
  const groupedRecommendations = {
    admin: combinedDoctorAmounts.filter((r) => r.type === 'admin'),
    doctors: combinedDoctorAmounts.filter((r) => r.type === 'doctor'),
    navigators: combinedDoctorAmounts.filter((r) => r.type === 'navigator'),
    campaigns: combinedDoctorAmounts.filter((r) => r.type === 'campaign'),
  };

  // Sort each group by amount descending
  Object.values(groupedRecommendations).forEach((group) => {
    group.sort((a, b) => b.amount - a.amount);
  });

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = contentRef.current;
    if (!content) return;

    const styles = `
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          margin: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th, td {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        th {
          background-color: #f8f9fa;
          font-weight: bold;
        }
        .section-header {
          background-color: #f8f9fa;
          font-weight: bold;
          padding: 8px;
          margin-top: 20px;
        }
        .amount {
          text-align: right;
          font-family: monospace;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Detalii plăți ${showPaid ? 'efectuate' : 'restante'}</title>
          ${styles}
        </head>
        <body>
          <h1>Detalii plăți ${showPaid ? 'efectuate' : 'restante'}</h1>
          ${content.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load
    setTimeout(() => {
      printWindow.print();
      // Close the window after printing (optional)
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {showPaid ? 'Detalii plăți efectuate' : 'Detalii plăți restante'}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="px-3 py-1 bg-[#024994] text-white rounded-lg hover:bg-[#024994]/90 transition flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Printează
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div ref={contentRef} className="overflow-y-auto max-h-[60vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nume
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sumă de plată
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Admin Section */}
                {groupedRecommendations.admin.length > 0 && (
                  <>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={2}
                        className="px-6 py-2 text-sm font-medium text-gray-500"
                      >
                        Platformă (10%)
                      </td>
                    </tr>
                    {groupedRecommendations.admin.map((rec, index) => (
                      <tr key={`admin-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rec.doctorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatAmount(rec.amount)} RON
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Participating Doctors */}
                {groupedRecommendations.doctors.length > 0 && (
                  <>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={2}
                        className="px-6 py-2 text-sm font-medium text-gray-500"
                      >
                        Medici participanți
                      </td>
                    </tr>
                    {groupedRecommendations.doctors.map((rec, index) => (
                      <tr key={`doctor-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rec.doctorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatAmount(rec.amount)} RON
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Navigators */}
                {groupedRecommendations.navigators.length > 0 && (
                  <>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={2}
                        className="px-6 py-2 text-sm font-medium text-gray-500"
                      >
                        Navigatori (10%)
                      </td>
                    </tr>
                    {groupedRecommendations.navigators.map((rec, index) => (
                      <tr key={`navigator-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rec.doctorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatAmount(rec.amount)} RON
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Campaigns */}
                {groupedRecommendations.campaigns.length > 0 && (
                  <>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={2}
                        className="px-6 py-2 text-sm font-medium text-gray-500"
                      >
                        Campanii publicitare (10%)
                      </td>
                    </tr>
                    {groupedRecommendations.campaigns.map((rec, index) => (
                      <tr key={`campaign-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rec.doctorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatAmount(rec.amount)} RON
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Închide
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const ALLOWED_EMAILS = [
  'ovidiu.mudura@yahoo.com',
  'tudorbolos2001@yahoo.com',
  'mihaicojocarulaboncz@yahoo.com',
  'sergiumurg@yahoo.com',
  'sergiu.murg@decizieoncologica.ro',
  'viman.diana@yahoo.ro',
  'flaviuscoser@gmail.com',
];

export default function Plati() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentStatus[]>([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPaidSummaryModal, setShowPaidSummaryModal] = useState(false);
  const [hidePaidEntries, setHidePaidEntries] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    checkAccess();
  }, [navigate]);

  useEffect(() => {
    if (selectedDate) {
      fetchPayments();
    }
  }, [selectedDate]);

  const checkAccess = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !ALLOWED_EMAILS.includes(user.email || '')) {
        navigate('/');
        return;
      }

      await fetchPayments();
    } catch (error) {
      console.error('Error checking access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      // Fetch financial calculations
      const { data: calculations, error: calcError } = await supabase
        .from('financial_calculations')
        .select(
          `
          *,
          doctor:profiles (
            id,
            prenume,
            nume
          )
        `
        )
        .order('date', { ascending: false });

      if (calcError) throw calcError;

      // Fetch payment status
      const { data: statusData, error: statusError } = await supabase
        .from('payment_status')
        .select('date, is_paid');

      if (statusError) throw statusError;

      const statusMap = new Map(
        statusData?.map((status) => [status.date, status.is_paid])
      );

      // Group calculations by date
      const paymentsByDate = (calculations || []).reduce(
        (acc: Record<string, PaymentStatus>, curr) => {
          const date = curr.date;
          if (!acc[date]) {
            acc[date] = {
              date,
              totalAmount: 0,
              isPaid: statusMap.get(date) || false,
              recommendations: [],
            };
          }

          // Add to total amount
          acc[date].totalAmount += curr.amount;

          // Add recommendation
          const doctorName = curr.doctor
            ? `${curr.doctor.nume} ${curr.doctor.prenume}`
            : curr.type === 'campaign'
            ? 'Campanie publicitară'
            : curr.type === 'navigator'
            ? curr.navigator_name || 'Navigator'
            : 'Ovidiu Mudura';

          const existingRec = acc[date].recommendations.find(
            (r) => r.doctorName === doctorName && r.type === curr.type
          );

          if (existingRec) {
            existingRec.amount += curr.amount;
          } else {
            acc[date].recommendations.push({
              doctorName,
              amount: curr.amount,
              type: curr.type,
            });
          }

          return acc;
        },
        {}
      );

      setPayments(Object.values(paymentsByDate));
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError('Error loading payment data');
    }
  };

  const togglePaymentStatus = async (date: string) => {
    try {
      const payment = payments.find((p) => p.date === date);
      if (!payment) return;

      const newStatus = !payment.isPaid;

      const { data: existingRecord } = await supabase
        .from('payment_status')
        .select('id')
        .eq('date', date)
        .single();

      if (existingRecord) {
        const { error: updateError } = await supabase
          .from('payment_status')
          .update({ is_paid: newStatus })
          .eq('date', date);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('payment_status')
          .insert({ date, is_paid: newStatus });

        if (insertError) throw insertError;
      }

      setPayments((prev) =>
        prev.map((payment) =>
          payment.date === date ? { ...payment, isPaid: newStatus } : payment
        )
      );

      setSuccess(
        `Status actualizat cu succes pentru ${new Date(date).toLocaleDateString(
          'ro-RO'
        )}`
      );
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Eroare la actualizarea statusului');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDateClick = (date: string) => {
    navigate(`/financiar?date=${date}`);
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateTotalUnpaid = () => {
    return payments
      .filter((payment) => !payment.isPaid)
      .reduce((total, payment) => total + payment.totalAmount, 0);
  };

  const calculateTotalPaid = () => {
    return payments
      .filter((payment) => payment.isPaid)
      .reduce((total, payment) => total + payment.totalAmount, 0);
  };

  const filteredPayments = hidePaidEntries
    ? payments.filter((payment) => !payment.isPaid)
    : payments;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-8 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white rounded-lg shadow-lg p-6"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div
            className="flex items-center justify-between mb-6"
            variants={fadeInUp}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#024994]" />
              <h1 className="text-2xl font-bold text-gray-900">
                Plăți Comisie Medicală
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPaidSummaryModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <Calculator className="w-4 h-4" />
                Total plăți efectuate
              </button>
              <button
                onClick={() => setHidePaidEntries(!hidePaidEntries)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  hidePaidEntries
                    ? 'bg-[#024994] text-white hover:bg-[#024994]/90'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                {hidePaidEntries
                  ? 'Afișează plățile efectuate'
                  : 'Ascunde plățile efectuate'}
              </button>
            </div>
          </motion.div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <p className="text-green-700">{success}</p>
            </div>
          )}

          <motion.div className="overflow-x-auto" variants={fadeInUp}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dată Comisie
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total de Plată
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.date} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDateClick(payment.date)}
                        className="text-sm font-medium text-[#024994] hover:text-[#024994]/80 flex items-center gap-1"
                      >
                        {new Date(payment.date).toLocaleDateString('ro-RO')}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatAmount(payment.totalAmount)} RON
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex justify-center">
                        <button
                          onClick={() => togglePaymentStatus(payment.date)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            payment.isPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {payment.isPaid ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              PLĂTIT
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-1" />
                              NEPLĂTIT
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    Total de plată
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <button
                      onClick={() => setShowSummaryModal(true)}
                      className="text-[#024994] hover:text-[#024994]/80 font-medium flex items-center justify-end gap-2 w-full"
                    >
                      {formatAmount(calculateTotalUnpaid())} RON
                      <Users className="w-4 h-4" />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">
                    Suma neplătită
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showSummaryModal && (
          <PaymentSummaryModal
            isOpen={showSummaryModal}
            onClose={() => setShowSummaryModal(false)}
            payments={payments}
            showPaid={false}
          />
        )}

        {showPaidSummaryModal && (
          <PaymentSummaryModal
            isOpen={showPaidSummaryModal}
            onClose={() => setShowPaidSummaryModal(false)}
            payments={payments}
            showPaid={true}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
